import * as THREE from "three";
import type { Cup } from "./types";
import { getCupTexture } from "./textures";

/**
 * Cup geometry registry. Each entry is a self-contained shape definition
 * (lathe profile + inner profile + handle params). Adding a new shape (e.g.
 * "espresso", "tall") is appending one entry — no cup.ts call-site changes
 * needed; cups in cups.json reference the entry by name.
 *
 * Geometries are built lazily on first reference and cached per shape; cups
 * of the same shape share both geometry and any cached material defaults.
 *
 * Note: physics in `src/physics.ts` currently hardcodes CUP_RADIUS / CUP_HEIGHT
 * matching "standard". When non-standard shapes ship, physics will need to
 * read from this registry too.
 */
type ShapeDef = {
  /** Lathe-swept profile of the outer body. Y goes from baseY to height. */
  bodyProfile: THREE.Vector2[];
  /** Inner cavity. Same lathe convention; sits inside bodyProfile. */
  innerProfile: THREE.Vector2[];
  /** Min Y of bodyProfile — cup sits on the shelf at this height. */
  baseY: number;
  /** Max Y of bodyProfile — used by layout to space rows. */
  height: number;
  /**
   * Handle parameters. Set to `null` for handle-less cups (e.g. some
   * espresso forms). Origin is in cup-local space (Y above baseY).
   */
  handle: {
    major: number;
    tube: number;
    origin: THREE.Vector3;
    arc: number;
  } | null;
};

const SHAPES: Record<string, ShapeDef> = {
  standard: {
    bodyProfile: buildStandardBodyProfile(),
    innerProfile: buildStandardInnerProfile(),
    baseY: 0,
    height: 1.12,
    handle: {
      major: 0.24,
      tube: 0.05,
      origin: new THREE.Vector3(0.5, 0.6, 0),
      arc: Math.PI,
    },
  },
};

const DEFAULT_SHAPE = "standard";
const DEFAULT_INNER_COLOR = 0xf6f3ec;
const DEFAULT_HANDLE_COLOR = 0xf2efe8;

/** Cached, per-shape compiled geometry. Built on first reference. */
const geometryCache: Map<
  string,
  {
    body: THREE.LatheGeometry;
    inner: THREE.LatheGeometry;
    handle: THREE.TorusGeometry | null;
  }
> = new Map();

// Default handle material — used when cup.handleColor is unset. Per-cup
// overrides clone this material with the parsed hex applied.
const HANDLE_MATERIAL = new THREE.MeshStandardMaterial({
  color: DEFAULT_HANDLE_COLOR,
  roughness: 0.5,
  metalness: 0.05,
});

export interface CupObject {
  group: THREE.Group;
  body: THREE.Mesh;
  cup: Cup;
}

export function createCupObject(cup: Cup): CupObject {
  const shape = getShape(cup.shape);
  const geom = getGeometryForShape(cup.shape ?? DEFAULT_SHAPE, shape);
  const texture = getCupTexture(cup);

  const bodyMaterial = new THREE.MeshStandardMaterial({
    map: texture,
    roughness: 0.45,
    metalness: 0.05,
  });

  const body = new THREE.Mesh(geom.body, bodyMaterial);
  body.castShadow = true;
  body.receiveShadow = true;
  body.userData.role = "body";

  // Per-cup inner material so each cup can carry its own glaze color.
  // Real ceramic interiors are matte — roughness 0.88 reads as glaze, not
  // plastic.
  const innerMaterial = new THREE.MeshStandardMaterial({
    color: parseHexColor(cup.innerColor) ?? DEFAULT_INNER_COLOR,
    roughness: 0.88,
    metalness: 0.0,
    // DoubleSide so the inside-facing surface is always opaque — looking
    // down into the cup at a steep angle, this stops the camera ray from
    // slipping past the inner wall and rendering the handle through the
    // cup's interior.
    side: THREE.DoubleSide,
  });
  const inner = new THREE.Mesh(geom.inner, innerMaterial);
  inner.castShadow = true;
  inner.receiveShadow = true;

  const group = new THREE.Group();
  group.add(body, inner);

  if (shape.handle && geom.handle) {
    const handleMaterial = cup.handleColor
      ? new THREE.MeshStandardMaterial({
          color: parseHexColor(cup.handleColor) ?? DEFAULT_HANDLE_COLOR,
          roughness: 0.5,
          metalness: 0.05,
        })
      : HANDLE_MATERIAL;
    const handle = new THREE.Mesh(geom.handle, handleMaterial);
    handle.castShadow = true;
    handle.position.copy(shape.handle.origin);
    group.add(handle);
    handle.userData.cupId = cup.id;
  }

  group.userData.cupId = cup.id;
  body.userData.cupId = cup.id;

  return { group, body, cup };
}

/**
 * Lookup a shape definition from the registry. Falls back to "standard" if
 * the cup's `shape` field references an entry that hasn't been added yet.
 */
function getShape(name: string | undefined): ShapeDef {
  if (name && SHAPES[name]) return SHAPES[name];
  if (name && !SHAPES[name]) {
    console.warn(
      `[cup] unknown shape "${name}", falling back to "${DEFAULT_SHAPE}"`,
    );
  }
  return SHAPES[DEFAULT_SHAPE];
}

function getGeometryForShape(name: string, shape: ShapeDef) {
  const cached = geometryCache.get(name);
  if (cached) return cached;

  // `phiStart=π` puts the texture seam at the back of the cup (cup-local
  // −Z), so canvas U=0.5 — the visual center of a stitched wrap — lands at
  // the front (cup-local +Z), which faces the camera after layout's +π/4
  // yaw. Any unavoidable mismatch where canvas U=0 meets U=1 hides at the
  // back, away from view.
  const body = new THREE.LatheGeometry(
    shape.bodyProfile,
    64,
    Math.PI,
    Math.PI * 2,
  );
  const inner = new THREE.LatheGeometry(shape.innerProfile, 48);

  let handle: THREE.TorusGeometry | null = null;
  if (shape.handle) {
    // Default torus arc starts at +X; rotating by -π/2 around Z shifts the
    // start to -Y, so the half-arc sweeps -Y → +X → +Y — i.e., a C opening
    // toward -X (the cup body).
    handle = new THREE.TorusGeometry(
      shape.handle.major,
      shape.handle.tube,
      16,
      48,
      shape.handle.arc,
    );
    handle.rotateZ(-Math.PI / 2);
  }

  const entry = { body, inner, handle };
  geometryCache.set(name, entry);
  return entry;
}

/**
 * Standard mug: profile starts at the base, sweeps up with a subtle
 * outward taper, then folds inward to form the rim's lip.
 */
function buildStandardBodyProfile(): THREE.Vector2[] {
  const points: THREE.Vector2[] = [];
  points.push(new THREE.Vector2(0.0, 0.0));
  points.push(new THREE.Vector2(0.45, 0.0));
  points.push(new THREE.Vector2(0.48, 0.05));
  points.push(new THREE.Vector2(0.5, 0.12));
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const y = 0.12 + t * 0.95;
    const r = 0.5 + Math.sin(t * Math.PI) * 0.02;
    points.push(new THREE.Vector2(r, y));
  }
  points.push(new THREE.Vector2(0.5, 1.1));
  points.push(new THREE.Vector2(0.49, 1.12));
  return points;
}

/**
 * Hollow inner surface so a peek over the rim reveals depth instead of a
 * solid plug. The profile starts at the inside base (a disc), runs up the
 * inner wall, and ends with a small outward bevel that bridges the body's
 * rim turn-in. Without that bevel a 45°-down camera ray slips through the
 * thin annular gap between r=0.44 and r=0.5 and renders the handle behind
 * the cup.
 */
function buildStandardInnerProfile(): THREE.Vector2[] {
  const points: THREE.Vector2[] = [];
  points.push(new THREE.Vector2(0.0, 0.06));
  points.push(new THREE.Vector2(0.42, 0.06));
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const y = 0.06 + t * 1.0;
    const r = 0.44;
    points.push(new THREE.Vector2(r, y));
  }
  points.push(new THREE.Vector2(0.47, 1.08));
  points.push(new THREE.Vector2(0.49, 1.1));
  return points;
}

function parseHexColor(hex: string | undefined): number | null {
  if (!hex) return null;
  const m = /^#?([a-f\d]{6})$/i.exec(hex);
  if (!m) return null;
  return parseInt(m[1], 16);
}

/**
 * Expose the registry to layout / physics so they can size around the
 * actual shape rather than hardcoding standard mug dimensions.
 */
export function getShapeDef(name: string | undefined): ShapeDef {
  return getShape(name);
}
